import classNames from "classnames";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { isTextTruncated } from "utilities/isTextTruncated";
import LoanStatusChip from "../loan-status-chip/LoanStatusChip";
import { LoanNavBaseState } from "../loan-status-chip/loanNavStatusMapping";
import "./loan-nav-item.scss";

export interface LoanNavItemProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly baseState: LoanNavBaseState;
  readonly isActive?: boolean;
  readonly onClick?: () => void;
  readonly className?: string;
}

export default function LoanNavItem({
  icon,
  label,
  baseState,
  isActive = false,
  onClick,
  className,
}: LoanNavItemProps) {
  const itemRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isLabelTruncated, setIsLabelTruncated] = useState(false);

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    const itemElement = itemRef.current;

    if (!labelElement) {
      return;
    }

    const updateTruncation = () => {
      setIsLabelTruncated(isTextTruncated(labelElement));
    };

    updateTruncation();

    const rafId = requestAnimationFrame(updateTruncation);

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateTruncation) : null;
    resizeObserver?.observe(labelElement);

    if (itemElement) {
      resizeObserver?.observe(itemElement);
    }

    const fontsReady = document.fonts?.ready;
    if (fontsReady) {
      void fontsReady.then(updateTruncation);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [label]);

  return (
    <button
      ref={itemRef}
      type="button"
      className={classNames("loan-nav-item", { "loan-nav-item--selected": isActive }, className)}
      onClick={onClick}
      aria-current={isActive ? "true" : undefined}
      title={isLabelTruncated ? label : undefined}
      data-testid="loan-nav-item"
    >
      <span className="loan-nav-item__content">
        <span className="loan-nav-item__row">
          <span className="loan-nav-item__icon" aria-hidden="true">
            {icon}
          </span>
          <span ref={labelRef} className="loan-nav-item__label label-large-medium">
            {label}
          </span>
        </span>
        <span className="loan-nav-item__chip">
          <LoanStatusChip baseState={baseState} />
        </span>
      </span>
    </button>
  );
}
